import axios from 'axios';
import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import './App.css';
import Router from './components/route/Router';

export const AuthContext = createContext();

const defaultMaintenanceMessage = 'Pagado is temporarily down for maintenance.';
const defaultMaintenance = {
    enabled: false,
    message: defaultMaintenanceMessage,
};

const initialState = {
    isAuthenticated: false,
    id: null,
    email: null,
    loading: true,
    maintenance: defaultMaintenance,
};

function normalizeMaintenance(value) {
    if (!value) {
        return defaultMaintenance;
    }

    return {
        enabled: Boolean(value.enabled),
        message: value.message || defaultMaintenanceMessage,
        updated_at: value.updated_at || null,
        updated_by: value.updated_by || null,
        updated_by_username: value.updated_by_username || null,
        updated_by_email: value.updated_by_email || null,
    };
}

function maintenanceFromResponse(data) {
    if (!data || typeof data !== 'object') {
        return null;
    }

    return data.maintenance || data.content || null;
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                isAuthenticated: true,
                id: action.payload.id,
                email: action.payload.email,
                loading: false,
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                id: null,
                email: null,
                loading: false,
            };
        case 'SET_MAINTENANCE':
            return {
                ...state,
                maintenance: normalizeMaintenance(action.payload),
            };
        default:
            return state;
    }
};

function App() {
    const [state, dispatch] = useReducer(reducer, initialState);

    const verifyAuth = useCallback(async () => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_SERVER}/api/auth/verify`);

            if (res.status === 200) {
                dispatch({
                    type: 'LOGIN',
                    payload: {
                        id: res.data.id,
                        email: res.data.email,
                    },
                });
                return;
            }

            throw new Error('Authentication failed');
        } catch (error) {
            const maintenance = maintenanceFromResponse(error.response?.data);

            if (maintenance) {
                dispatch({ type: 'SET_MAINTENANCE', payload: maintenance });
            }

            dispatch({ type: 'LOGOUT' });
        }
    }, []);

    useEffect(() => {
        const loadMaintenanceStatus = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/maintenance/status`);
                dispatch({ type: 'SET_MAINTENANCE', payload: res.data.content });
            } catch (_error) {
                dispatch({ type: 'SET_MAINTENANCE', payload: defaultMaintenance });
            }
        };

        loadMaintenanceStatus();
    }, []);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                const data = error.response?.data;
                const maintenance = maintenanceFromResponse(data);

                if (error.response?.status === 503 && data?.code === 'MAINTENANCE_MODE' && maintenance) {
                    dispatch({ type: 'SET_MAINTENANCE', payload: maintenance });
                    dispatch({ type: 'LOGOUT' });

                    if (window.location.pathname !== '/login') {
                        window.location.assign('/login');
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    useEffect(() => {
        if (state.loading) {
            verifyAuth();
        }
    }, [state.loading, verifyAuth]);

    useEffect(() => {
        if (!state.isAuthenticated) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            void verifyAuth();
        }, 30000);

        return () => window.clearInterval(interval);
    }, [state.isAuthenticated, verifyAuth]);

    if (state.loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="App">
            <AuthContext.Provider value={{ state, dispatch }}>
                <Router />
            </AuthContext.Provider>
        </div>
    );
}

export default App;
